import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { theme as lightTheme, darkTheme } from './theme';
import { AnalyticsDashboard } from './components/analytics/AnalyticsDashboard';
import { PRSubmissionInterface } from './components/submission/PRSubmissionInterface';
import { DistributionInterface } from './components/distribution/DistributionInterface';
import ClientAuth from './components/auth/ClientAuth';
import VerifyEmail from './components/auth/VerifyEmail';
import Layout from './components/layout/Layout';
import HomePageEnhanced from './components/pages/HomePageEnhanced';
import Checkout from './components/payments/Checkout';
import PaymentSuccess from './components/pages/PaymentSuccess';
import PRLibrary from './components/pages/PRLibrary';
import Campaigns from './components/pages/Campaigns';
import MediaContacts from './components/pages/MediaContacts';
import Publications from './components/pages/Publications';
import Verification from './components/pages/Verification';
import Payments from './components/pages/Payments';
import Credits from './components/pages/Credits';
import Reports from './components/pages/Reports';
import Timeline from './components/pages/Timeline';
import { AdvancedFeatures } from './components/advanced/AdvancedFeatures';
import Settings from './components/pages/Settings';
import HelpSupport from './components/pages/HelpSupport';
import Dashboard from './components/pages/Dashboard';
import Notifications from './components/pages/Notifications';
import MediaPublishedUrl from './components/pages/MediaPublishedUrl';
import MediaKit from './components/pages/MediaKit';
import Pricing from './components/pages/Pricing';
import DistributionAccept from './components/pages/DistributionAccept';
import DistributionDeny from './components/pages/DistributionDeny';
import TeamInvitationAccept from './components/pages/TeamInvitationAccept';
import TeamInvitationReject from './components/pages/TeamInvitationReject';
import NotFound from './components/pages/NotFound';
import { AccessGateProvider } from './contexts/AccessGateContext';
import { AuthProvider } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminAuth from './components/admin/AdminAuth';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
import FloatingChatbot from './components/chatbot/FloatingChatbot';

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    // Load saved dark mode preference from localStorage
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Load dark mode preference on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      setDarkMode(savedMode === 'true');
    }
  }, []);

  // Save dark mode preference when it changes
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  // Toggle dark mode
  const handleToggleDarkMode = () => {
    setDarkMode((prev) => !prev);
  };

  // Select theme based on dark mode state
  const currentTheme = useMemo(() => {
    return darkMode ? darkTheme : lightTheme;
  }, [darkMode]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <AccessGateProvider>
        <AuthProvider>
          <AdminProvider>
            <Router>
          <Routes>
            <Route path="/" element={<HomePageEnhanced />} />
            <Route path="/auth" element={<ClientAuth />} />
            <Route path="/login" element={<ClientAuth />} />
            <Route path="/register" element={<ClientAuth />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/checkout" element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            } />
            {/* Create PR Routes */}
            <Route path="/create-pr" element={
              <ProtectedRoute>
                <Navigate to="/create-pr/upload-compose" replace />
              </ProtectedRoute>
            } />
            <Route path="/create-pr/:step" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <PRSubmissionInterface />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/distribute" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <DistributionInterface />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <AnalyticsDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/pr-library" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <PRLibrary />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/campaigns" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Campaigns />
                </Layout>
              </ProtectedRoute>
            } />
            {/* <Route path="/media-contacts" element={
              <ProtectedRoute>
                <Layout>
                  <MediaContacts />
                </Layout>
              </ProtectedRoute>
            } /> */}
            <Route path="/publications" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Publications />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/verification" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Verification />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Notifications />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/payments" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Payments />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/credits" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Credits />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/pricing" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Pricing />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Reports />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/timeline" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Timeline />
                </Layout>
              </ProtectedRoute>
            } />
            {/* Advanced Features Routes */}
            <Route path="/advanced" element={
              <ProtectedRoute>
                <Navigate to="/advanced/copilot" replace />
              </ProtectedRoute>
            } />
            <Route path="/advanced/:tab" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <AdvancedFeatures />
                </Layout>
              </ProtectedRoute>
            } />
            {/* Settings Routes */}
            <Route path="/settings" element={
              <ProtectedRoute>
                <Navigate to="/settings/profile" replace />
              </ProtectedRoute>
            } />
            <Route path="/settings/:tab" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/help" element={
              <ProtectedRoute>
                <Layout darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode}>
                  <HelpSupport />
                </Layout>
              </ProtectedRoute>
            } />
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminAuth />} />
            <Route path="/admin/dashboard" element={
              <AdminProtectedRoute>
                <AdminDashboard />
              </AdminProtectedRoute>
            } />
            {/* Public route for publishers to submit their published article URL */}
            <Route path="/media-channel/published" element={<MediaPublishedUrl />} />
            {/* Public routes for distribution accept/deny (from email links) */}
            <Route path="/distribution/accept/:distributionId" element={<DistributionAccept />} />
            <Route path="/distribution/deny/:distributionId" element={<DistributionDeny />} />
            {/* Public routes for team invitation accept/reject (from email links) */}
            <Route path="/team/invitation/accept" element={<TeamInvitationAccept />} />
            <Route path="/team/invitation/reject" element={<TeamInvitationReject />} />
            {/* Public route for media kit pages */}
            <Route path="/media-kit" element={<MediaKit />} />
            <Route path="/media-kit/:prId" element={<MediaKit />} />
            <Route path="/media-kit/:companySlug/:campaignId" element={<MediaKit />} />
            {/* 404 Page - Catch all unmatched routes */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          {/* Floating AI Chatbot - Available on all pages when authenticated */}
          <FloatingChatbot />
            </Router>
          </AdminProvider>
        </AuthProvider>
      </AccessGateProvider>
    </ThemeProvider>
  );
}

export default App;